// import { EMAIL_VALIDATE_REGEX } from '@/configs/constants';
import ActiveIcon from '@/assets/icons/ActiveIcon';
import InactiveIcon from '@/assets/icons/InactiveIcon';
import TrashIcon from '@/assets/icons/TrashIcon';
import UploadFileIcon from '@/assets/icons/UploadIcon';
import { Button } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { Label } from '@/components/common/Label';
import { Tabs, TabsList, TabsTrigger } from '@/components/common/Tabs';
import Table from '@/components/table/Table';
import { ACTIVITY_COLOR, DEFAULT_SELECT_PAGE_SIZES, ITEM_ACTIVITYS, ITEMS_STATUS, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { ITEM_ACTIVITY, ITEM_STATUS, ITEM_TYPE, ROLE } from '@/types/enums';
import { toastify } from '@/utils/toastify';
import { convertDate } from '@/utils/utils';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { MapPinned, Plus, Search, SquarePen } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import RejectForm from './components/RejectForm';
import UploadFileForm from './components/UploadFileForm';
import ViewItem from './components/ViewItem';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/common/Input';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';

// MEO landmark ads (Phase 1, P4, 2026-05-06): item_type 別のタブ分割。
// 数値は backend (items.item_type SMALLINT) と一致:
//   0 = PERSONAL (= 既存 NOT_SET、個人ユーザのアイテム)
//   1 = COMMERCIAL_POSTCARD (= 既存 COUPON、紅型ポストカード型おみくじ)
//   2 = TOURIST_INFO (Phase 2 以降の予定枠、現状空)
// Spec: design/meo-landmark-ads-spec.md §UX/UI §管理者
const ITEM_TYPE_TAB = {
    ALL: 'all',
    PERSONAL: '0',
    COMMERCIAL_POSTCARD: '1',
    TOURIST_INFO: '2'
} as const;
type ItemTypeTab = typeof ITEM_TYPE_TAB[keyof typeof ITEM_TYPE_TAB];

export default observer(function ItemManagementPage() {
    // hooks
    const { t } = useTranslation();

    const nav = useNavigate();

    // store
    const {
        itemStore: { getAllItem, deleteItem, approveItem, clean, totalCoupon, totalCouponUsed, items, defaultPaging, paging, totalRecord, keepSearchParams, setObservable },
        modalStore: { showAlertModal }
    } = useStore();

    const validateSchema = yup.object().shape({
        title: yup.string(),
        status: yup.string(),
        activity: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        defaultValues: {
            title: keepSearchParams?.title ?? undefined,
            status: keepSearchParams?.status ?? 'all',
            activity: keepSearchParams?.activity ?? 'all'
        }
    });
    const [rowSelected, setRowSelected] = useState({});

    // P4 (2026-05-06): 選択中の item_type タブ。keepSearchParams.item_type に同期して
    // ナビゲーション戻り時にもタブが復元される。
    const [activeTab, setActiveTab] = useState<ItemTypeTab>(
        () => (keepSearchParams?.item_type != null ? String(keepSearchParams.item_type) as ItemTypeTab : ITEM_TYPE_TAB.ALL)
    );

    // lifecycle
    useEffect(() => {
        getAllItem(keepSearchParams, paging);
        // return () => {
        //     clean();
        // };
    }, []);

    // function
    const filterObject = (obj) => {
        return Object.fromEntries(
            Object.entries(obj).filter(([key, value]) => !(['activity', 'status'].includes(key) && value === 'all'))
        );
    };

    // search params + 現在のタブ (item_type) を統合して backend クエリ用 payload を作る。
    const buildQuery = (searchParams: any, tab: ItemTypeTab) => {
        const filtered = filterObject(searchParams ?? {});
        return {
            ...filtered,
            title: filtered?.title === '' ? undefined : filtered?.title,
            // 'all' タブは item_type フィルタなし。それ以外は数値として送る。
            ...(tab === ITEM_TYPE_TAB.ALL ? {} : { item_type: Number(tab) })
        };
    };

    const onSearch = async (searchParams?: any) => {
        const query = buildQuery(searchParams, activeTab);
        setObservable('keepSearchParams', query, { isMergeObject: false });
        await flowResult(getAllItem(query, defaultPaging));
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getAllItem(keepSearchParams, newPaging);
    };

    const handleTabChange = async (next: string) => {
        const tab = next as ItemTypeTab;
        setActiveTab(tab);
        const query = buildQuery(form.getValues(), tab);
        setObservable('keepSearchParams', query, { isMergeObject: false });
        await flowResult(getAllItem(query, defaultPaging));
    };

    const columnsItem: ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'No.',
            size: 50,
            enableSorting: false,
            cell: ({ row }) => {
                const { pageIndex, pageSize } = paging.pagination;
                const index = (row.index + 1) + (pageIndex * pageSize);
                return index;
            }
        },
        {
            // 実 item.id (No. は表示連番で ID とは別 — user 一覧と同方針、2026-07-07 pitfall batch)。
            accessorKey: 'realId',
            header: 'ID',
            size: 60,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='font-mono text-gray-600'>{original.id}</div>
            )
        },
        {
            accessorKey: 'item',
            header: t('words_title.item_name'),
            size: 700,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className='flex items-center gap-2'>
                        <div onClick={() => handleViewItem(original)} className='text-bg-button-blue cursor-pointer underline-offset-4 hover:underline'>{original.item}</div>
                        {/* イベント用マップ非表示フラグ (2026-07-22 spec §2-3) */}
                        {original.hiddenOnMap === true && (
                            <span className='shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300'>マップ非表示</span>
                        )}
                    </div>
                );
            }

        },
        {
            accessorKey: 'itemType',
            header: t('words_title.item_type'),
            size: 500,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <>
                    {original.itemType === ITEM_TYPE.COUPON ? t('words_title.coupon') : ''}
                </>
            )
        },
        {
            accessorKey: 'userCreated',
            header: t('words_title.client_name'),
            size: 600,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className=''>{original?.userCreated?.role_id === ROLE.ADMIN ? '' : original?.userCreated?.username}</div>
                );
            }
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.create_date'),
            size: 550,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className=''>
                    <div>
                        {convertDate(original.createdAt)?.date}
                    </div>
                    <div>
                        {convertDate(original.createdAt)?.time}
                        <span className='text-timezone ml-1'>{convertDate(original?.createdAt)?.ampm}</span>
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'updatedAt',
            header: t('words_title.last_update'),
            size: 450,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className=''>
                    <div>
                        {convertDate(original?.updatedAt)?.date}
                    </div>
                    <div>
                        {convertDate(original?.updatedAt)?.time}
                        <span className='text-timezone ml-1'>{convertDate(original?.updatedAt)?.ampm}</span>
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'activity',
            header: t('words_title.activity'),
            size: 500,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const activityItem = ACTIVITY_COLOR.find(item => item.id === original?.activity);

                return (
                    <div className='flex items-center'>
                        {activityItem && (
                            <>
                                <Button variant='default' className={`${activityItem?.style} cursor-default w-[145px] h-[31px] text-white !rounded-[30px] text-[13px]`}>
                                    {t(activityItem?.name)}
                                </Button>
                            </>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'status',
            header: t('words_title.status'),
            size: 500,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                let statusItem = ITEMS_STATUS.find(item => item.id === original?.status);
                // if (original?.status === ITEM_STATUS.UN_PUBLISHED && original?.userCreated?.role_id === ROLE.ADMIN) {
                //     statusItem = {
                //         id: ITEM_STATUS.UN_PUBLISHED,
                //         name: 'field.unpublish',
                //         style: 'bg-[#5A5A5A]',
                //         textColor: 'text-[#5A5A5A]'
                //     };
                // }
                // if (original?.status === ITEM_STATUS.UN_PUBLISHED && original?.userCreated?.role_id === ROLE.END_USER) {
                //     statusItem = {
                //         id: ITEM_STATUS.PRIVATE,
                //         name: 'field.private',
                //         style: 'bg-[#A90E0E]',
                //         textColor: 'text-[#A90E0E]'
                //     };
                // }
                return (
                    <div className='flex items-center'>
                        {statusItem && (
                            <>
                                <div className={`${statusItem.style} size-[9px] rounded-full mr-1`}></div>
                                <span className={`${statusItem.textColor}`}>{t(statusItem.name)}</span>
                            </>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'action',
            header: t('words_title.action_2'),
            size: 600,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='grid grid-cols-3'>
                    <div className='flex justify-start'>
                        {
                            (
                                (original.activity === ITEM_ACTIVITY.PRIVATE__REPORT && original.status === ITEM_STATUS.PRIVATE) ||
                                (original.activity === ITEM_ACTIVITY.PRIVATE__EXPIRED && original.status === ITEM_STATUS.PRIVATE) ||
                                (original.activity === ITEM_ACTIVITY.PRIVATE__WITHDRAWAL && original.status === ITEM_STATUS.PRIVATE)) ?
                                (
                                    <Button
                                        onClick={() => handleConfirmApprove(original.id)}
                                        size='icon'
                                        variant='ghost'
                                        className='rounded-full bg-background-active flex-none'
                                    >
                                        <ActiveIcon className='size-[20px]' />
                                    </Button>
                                ) :
                                (original.status === ITEM_STATUS.PUBLISHED &&
                                    (original.activity === ITEM_ACTIVITY.PUBLISHED__RESTORE || original.activity === ITEM_ACTIVITY.PUBLISHED__ON_MAP)) ?
                                    (
                                        <Button
                                            onClick={() => handleConfirmReject(original.id)}
                                            size='icon'
                                            variant='ghost'
                                            className='rounded-full bg-background-inactive flex-none'
                                        >
                                            <InactiveIcon className='size-[20px]' />
                                        </Button>
                                    ) :
                                    null
                        }
                    </div>

                    {
                        !(original.activity === ITEM_ACTIVITY.PUBLISHED__GET_IT || original.activity === ITEM_ACTIVITY.PUBLISHED__USED) && (
                            <div className='flex justify-start'>
                                <Button
                                    onClick={() => handleDeleteItem(original.id, original.item)}
                                    size='icon'
                                    variant='ghost'
                                    className='rounded-full bg-background-inactive flex-none'
                                >
                                    <TrashIcon className='size-[20px]' color='#FE1212' />
                                </Button>
                            </div>
                        )
                    }

                    <div className='flex justify-start'>
                        {original?.userCreated?.role_id === ROLE.ADMIN && !(original.activity === ITEM_ACTIVITY.PUBLISHED__GET_IT || original.activity === ITEM_ACTIVITY.PUBLISHED__USED) && (
                            <Button
                                onClick={() => handleUpdateItem(original.id)}
                                title={t('field.active')}
                                size='icon'
                                variant='ghost'
                                className='rounded-full bg-background-view flex-none'
                            >
                                <SquarePen className='size-[20px]' color='#1C50FF' />
                            </Button>
                        )}
                    </div>
                </div>

            )
        }
    ];

    // function
    const handleDeleteItem = (id: string, name: string) => {
        showAlertModal({
            title: '確認',
            size: 'sm',
            headerClassName: 'border-b border-b-border-7',
            content: t('sentences.confirm_delete_item'),
            onSave: async () => {
                const res = await flowResult(deleteItem(id));
                if (res) {
                    toastify('success', t('messages.delete_item_success', {
                        itemname: name
                    }));
                    getAllItem(undefined, paging);
                }
            }
        });
    };
    const handleViewItem = (item: any) => {
        showAlertModal({
            title: t('words_title.view', { name: item?.item }),
            size: '3xl',
            headerClassName: 'border-b border-b-border-7',
            contentClassName: 'pb-0 !overflow-y-scroll',
            footerClassName: 'hidden',
            content: <ViewItem item={item} />
        });
    };

    const handleUpdateItem = (id: number) => {
        nav(`${ROUTES.itemManagement.href}/${ROUTES.updateItem.href}/${id}`);
    };

    const handleUploadCSV = () => {
        showAlertModal({
            footerClassName: 'hidden',
            title: t('words_title.upload_data'),
            size: '3xl',
            contentClassName: 'pb-0',
            headerClassName: 'border-b border-b-border-7',
            content: <UploadFileForm />
        });
    };

    const handleConfirmApprove = (id: number) => {
        showAlertModal({
            title: t('words_title.confirm'),
            size: 'sm',
            headerClassName: 'border-b border-b-border-7',
            content: t('sentences.confirm_republish'),
            onSave: async () => {
                const res = await flowResult(approveItem(id));
                if (res && res?.ok) {
                    toastify('success', '選択されたアイテムは再公開されました。');
                    getAllItem(keepSearchParams, paging);
                } else {
                    toastify('error', res?.message);
                }
            }
        });
    };

    const handleConfirmReject = (id: number) => {
        showAlertModal({
            title: t('sentences.confirm_withdrawal'),
            size: '3xl',
            headerClassName: 'border-b border-b-border-7',
            contentClassName: 'pb-0',
            footerClassName: 'hidden',
            content: <RejectForm id={id} />
        });
    };

    const handleAddItem = () => {
        nav(`${ROUTES.itemManagement.href}/${ROUTES.addItem.href}`);
    };

    return (
        <>
            <div className='layout-card'>
                {/* P4 (2026-05-06): item_type 別タブ。spec §UX §管理者 のタブ分割要件を満たす。
                    backend は items.item_type filter を既に受け付けるので UI 側の wire-up のみ。
                    変更履歴: 初版で variant 未指定だとほぼ無スタイルでクリックできない見た目になっていた問題を修正
                    (= 既存 user/list の userTab variant + flex 等幅レイアウト + 縦区切りに合わせた)。 */}
                <Tabs value={activeTab} onValueChange={handleTabChange} className='mb-4'>
                    <TabsList variant='userTab' data-orientation='horizontal' className='flex w-full'>
                        <TabsTrigger
                            variant='userTab'
                            value={ITEM_TYPE_TAB.ALL}
                            className='flex-1 cursor-pointer border-r border-r-border-1 last:border-r-0'
                        >
                            全て
                        </TabsTrigger>
                        <TabsTrigger
                            variant='userTab'
                            value={ITEM_TYPE_TAB.PERSONAL}
                            className='flex-1 cursor-pointer border-r border-r-border-1 last:border-r-0'
                        >
                            個人
                        </TabsTrigger>
                        <TabsTrigger
                            variant='userTab'
                            value={ITEM_TYPE_TAB.COMMERCIAL_POSTCARD}
                            className='flex-1 cursor-pointer border-r border-r-border-1 last:border-r-0'
                        >
                            商業ポストカード
                        </TabsTrigger>
                        <TabsTrigger
                            variant='userTab'
                            value={ITEM_TYPE_TAB.TOURIST_INFO}
                            className='flex-1 cursor-pointer border-r border-r-border-1 last:border-r-0'
                        >
                            観光情報
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSearch)}>
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                            <div className='flex items-center gap-4'>
                                <Label className='text-[13px]'>{t('words_title.title')}</Label>
                                <FormField
                                    control={form.control}
                                    name='title'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 w-full'>
                                            <FormControl>
                                                <Input
                                                    className='h-[3.25rem]'
                                                    {...field}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='flex items-center gap-4'>
                                <Label className='text-[13px]'>{t('words_title.status')}</Label>
                                <FormField
                                    control={form.control}
                                    name='status'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 w-full'>
                                            <FormControl>
                                                <Combobox
                                                    className='h-[3.25rem]'
                                                    placeholder='All'
                                                    value={String(field.value)}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('status', item?.code);
                                                    }}
                                                    items={
                                                        [{ id: 'all', name: 'field.all' }, ...ITEMS_STATUS].map(item => ({
                                                            label: t(`${item.name}`),
                                                            value: String(item?.id),
                                                            code: item.id
                                                        }))
                                                    }
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='flex items-center gap-4'>
                                <Label className='text-[13px]'>{t('words_title.activity')}</Label>
                                <FormField
                                    control={form.control}
                                    name='activity'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 w-full'>
                                            <FormControl>
                                                <Combobox
                                                    className='h-[3.25rem]'
                                                    value={String(field.value)}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('activity', item?.code);
                                                    }}
                                                    items={
                                                        [{ id: 'all', name: 'field.all' }, ...ITEM_ACTIVITYS].map(item => ({
                                                            label: t(`${item.name}`),
                                                            value: String(item?.id),
                                                            code: item.id
                                                        }))
                                                    }
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className='mt-6 flex justify-center items-center'>
                            <Button size='xl' variant='active' className='h-[3.25rem] w-[13.5rem] flex items-center space-x-2'>
                                <Search className='size-[22px]' />
                                <p className='text-[16px] font-semibold'>{t('button.search')}</p>
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
            <div className='layout-card mt-6'>
                <Table
                    columns={columnsItem}
                    data={items}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    onStateChange={onStateChange}
                    rowCount={totalRecord}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    // getRowId={row => row.id?.toString()}
                    // enableRowSelection={true}
                    // onRowSelectionChange={setRowSelected}
                    showButtonAction={(
                        <div className='flex items-center gap-4 mt-4 md:mt-0'>
                            {/* 配置確認導線 (2026-07-22 spec §2-4): items + landmarks を一括プロットする運用マップへ */}
                            <Button onClick={() => nav(ROUTES.operationsMap.href)} size='xl' variant='outline' className='h-[3.25rem] w-1/2 point-450:w-[13.5rem] text-[13px] point-450:text-[16px] flex items-center space-x-1.5'>
                                <MapPinned className='size-[22px]' />
                                <p className='font-semibold'>{t('button.view_operations_map')}</p>
                            </Button>
                            <Button onClick={() => handleUploadCSV()} size='xl' variant='active' className='h-[3.25rem] w-1/2 point-450:w-[13.5rem] text-[13px] point-450:text-[16px] flex items-center space-x-1.5 bg-bg-button-blue'>
                                <UploadFileIcon className='size-[22px]' />
                                <p className='font-semibold'>{t('button.upload_csv')}</p>
                            </Button>
                            <Button onClick={() => handleAddItem()} size='xl' variant='active' className='h-[3.25rem] w-1/2  point-450:w-[13.5rem] text-[13px] point-450:text-[16px] flex items-center space-x-1.5'>
                                <Plus className='size-[22px]' />
                                <p className='font-semibold'>{t('button.add_new_item')}</p>
                            </Button>
                        </div>
                    )}
                    showResults={(
                        <div className='flex items-center'>
                            <div className='text-[15px] font-medium text-black-2'>
                                {t('sentences.number_coupons')}
                                {': '}
                                <span className='text-active text-[17px] font-semibold'>{totalCoupon}</span>
                            </div>
                            <div className='h-[19px] w-[1px] bg-border-7 mx-2 point-417:mx-4 md:mx-6 hidden point-375:block'></div>
                            <div className='text-[15px] font-medium text-black-2'>
                                {t('sentences.number_coupons_used')}
                                {': '}
                                <span className='text-active text-[17px] font-semibold'>{totalCouponUsed}</span>
                            </div>
                        </div>
                    )}
                />
            </div>
        </>
    );
});
